import React, { createContext, useContext, useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { GraphQLErrors } from "@apollo/client/errors";
import {
  getViewer_viewer_lists as viewerLists,
  getViewer_viewer as Viewer,
} from "types/gql-types";
import { sha256Hex } from "utils/hash";
import { initialStepData, StepData } from ".";

type StepContextType = {
  stepData: StepData;
  setStepData: React.Dispatch<React.SetStateAction<StepData>>;
  viewer: Viewer;
  stepName: string;
  setStepName: React.Dispatch<React.SetStateAction<string>>;
  importErrors: GraphQLErrors;
  setImportErrors: React.Dispatch<React.SetStateAction<GraphQLErrors>>;
};

// eslint-disable-next-line no-undefined
const StepContext = createContext<StepContextType | undefined>(undefined);

export const useStepData = (): StepContextType => {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error("useStepData must be used within a StepProvider");
  }
  if (!context.viewer) {
    throw new Error("useStepData must be used within a StepProvider with viewer");
  }
  if (!context.viewer.lists) {
    throw new Error("useStepData must be used within a StepProvider with viewer.lists");
  }
  if (!context.viewer.lists.length) {
    throw new Error("useStepData must be used within a StepProvider with an accessible list");
  }
  return context;
};

export const getListName = (lists?: viewerLists[] | null, listId?: string): string => {
  if (!lists) {
    return "Unknown List";
  }

  if (!listId) {
    if (lists.length === 1) {
      return lists[0].name;
    }
    return "No List Selected";
  }

  return lists.find(list => list.id === listId)?.name || "Unknown List";
};

type StepProviderProps = {
  children: React.ReactNode;
  viewer: Viewer
};

export const StepProvider: React.FC<StepProviderProps> = ({ children, viewer }) => {
  const [stepData, setStepData] = useState<StepData>(initialStepData);
  const [importErrors, setImportErrors] = useState<GraphQLErrors>([]);
  const [stepName, setStepName] = useState<string>("Upload");
  const posthog = usePostHog();

  useEffect(() => {
    let cancelled = false;
    // Send a pseudonymous hash instead of the raw email so we keep stable
    // person de-duplication without transmitting PII to PostHog.
    sha256Hex(viewer.email).then(emailHash => {
      if (cancelled) return;
      posthog.identify(viewer.id, {
        organization: viewer.organizationId,
        ...(emailHash ? { emailHash } : {}),
      });
    });
    posthog.capture("import_step", {
      stepName,
      listName: getListName(viewer.lists, stepData.listId),
    });
    return () => {
      cancelled = true;
    };
  }, [stepName]);

  return (
    <StepContext.Provider value={{
      stepData,
      setStepData,
      viewer,
      stepName,
      setStepName,
      importErrors,
      setImportErrors,
    }}>
      {children}
    </StepContext.Provider>
  );
};
